/**
 * Default keyboard shortcuts for the dashboard. Same combo format as
 * the office system so users muscle-memorise one set across both
 * products:
 *   - modifiers in this order: mod, shift, alt
 *     (mod = Cmd on macOS, Ctrl elsewhere)
 *   - key last, lowercased ASCII
 *   - joined with `+` (e.g. `mod+k`, `mod+shift+t`)
 */

export interface ShortcutDef {
  action_key: string;
  combo: string;
}

export const DEFAULT_SHORTCUTS: ShortcutDef[] = [
  { action_key: 'action:quick-search',     combo: 'mod+k' },
  { action_key: 'nav:/dashboard',          combo: 'mod+shift+d' },
  { action_key: 'nav:/audits',             combo: 'mod+shift+a' },
  { action_key: 'nav:/cross-audits',       combo: 'mod+shift+x' },
  { action_key: 'nav:/trial-balance',      combo: 'mod+shift+t' },
  { action_key: 'nav:/profit-loss',        combo: 'mod+shift+p' },
  { action_key: 'nav:/journal',            combo: 'mod+shift+j' },
  { action_key: 'nav:/expenses',           combo: 'mod+shift+e' },
  { action_key: 'nav:/customers',          combo: 'mod+shift+c' },
  { action_key: 'nav:/suppliers',          combo: 'mod+shift+s' },
  { action_key: 'nav:/chat',               combo: 'mod+shift+m' },
];

const ORDER: Record<string, number> = { mod: 0, shift: 1, alt: 2 };

/** Pretty-print a combo for the UI (⌘⇧K on mac, Ctrl+Shift+K elsewhere). */
export function formatCombo(combo: string): string {
  const isMac = typeof navigator !== 'undefined'
    && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '');
  const parts = combo.split('+').filter(Boolean);
  return parts.map((p) => {
    if (p === 'mod') return isMac ? '⌘' : 'Ctrl';
    if (p === 'shift') return isMac ? '⇧' : 'Shift';
    if (p === 'alt') return isMac ? '⌥' : 'Alt';
    if (p === 'arrowup') return '↑';
    if (p === 'arrowdown') return '↓';
    if (p === 'arrowleft') return '←';
    if (p === 'arrowright') return '→';
    if (p === 'escape') return 'Esc';
    if (p === ' ') return 'Space';
    return p.length === 1 ? p.toUpperCase() : p[0].toUpperCase() + p.slice(1);
  }).join(isMac ? '' : '+');
}

/** Normalize a KeyboardEvent into the combo string used by the registry.
 * Returns null for modifier-only keystrokes (pressing Ctrl alone, etc). */
export function comboFromEvent(e: KeyboardEvent): string | null {
  const k = e.key;
  if (k === 'Control' || k === 'Shift' || k === 'Alt' || k === 'Meta') return null;
  const mods: string[] = [];
  if (e.metaKey || e.ctrlKey) mods.push('mod');
  if (e.shiftKey) mods.push('shift');
  if (e.altKey) mods.push('alt');
  mods.sort((a, b) => ORDER[a] - ORDER[b]);
  const key = k.toLowerCase();
  return [...mods, key].join('+');
}

/** Forward map: action_key → combo. */
export function buildActionToCombo(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of DEFAULT_SHORTCUTS) out[s.action_key] = s.combo;
  return out;
}

/** Reverse map for O(1) lookup during keydown. */
export function buildComboToAction(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of DEFAULT_SHORTCUTS) out[s.combo] = s.action_key;
  return out;
}
